import React from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
} from '@mui/material';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { vehiclesApi } from '../services/api';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';

const VehicleInspectionsDashboardWidget: React.FC = () => {
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard-vehicle-inspections'],
    queryFn: () => vehiclesApi.getInspections({ is_exported: false }),
  });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load vehicle inspections. Please try again later.
      </Alert>
    );
  }

  const pendingCount = data?.inspections.length || 0;
  const recentInspections = data?.inspections.slice(0, 5) || [];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalShippingIcon color="primary" sx={{ fontSize: 28 }} />
          <Typography variant="h5" component="h2">
            Vehicle Inspections
          </Typography>
        </Box>
        <Button
          variant="outlined"
          endIcon={<ArrowForwardIcon />}
          onClick={() => navigate('/vehicle-docs')}
        >
          View All
        </Button>
      </Box>

      {/* Summary Card */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: pendingCount > 0 ? '#fff3e0' : '#e8f5e9' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
              {pendingCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pending Inspections
            </Typography>
          </Box>
          {pendingCount > 0 ? (
            <PendingIcon sx={{ fontSize: 48, color: 'warning.main' }} />
          ) : (
            <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
          )}
        </Box>
      </Paper>

      {/* Recent Inspections List */}
      {recentInspections.length > 0 ? (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Recent Pending Inspections
          </Typography>
          <Paper>
            <List disablePadding>
              {recentInspections.map((inspection, index) => (
                <React.Fragment key={inspection.id}>
                  {index > 0 && <Divider />}
                  <ListItem>
                    <ListItemIcon>
                      <LocalShippingIcon color={inspection.unit_type === 'TRUCK' ? 'primary' : 'secondary'} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {inspection.unit_number}
                          </Typography>
                          <Chip
                            label={inspection.unit_type === 'TRUCK' ? 'Truck' : 'Trailer'}
                            size="small"
                            color={inspection.unit_type === 'TRUCK' ? 'primary' : 'secondary'}
                          />
                        </Box>
                      }
                      secondary={`Inspected by ${inspection.inspected_by.first_name} ${inspection.inspected_by.last_name}`}
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Paper>
          {pendingCount > 5 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
              + {pendingCount - 5} more pending inspections
            </Typography>
          )}
        </>
      ) : (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            All inspections exported! No pending inspections.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<LocalShippingIcon />}
            onClick={() => navigate('/vehicle-docs')}
            sx={{ mt: 2 }}
          >
            Add New Inspection
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default VehicleInspectionsDashboardWidget;
